import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/services/api";
import { API_ENDPOINTS } from "@/config/api";
import { getOpenBetsForMarket, voidSingleOpenBet } from "@/services/api";
import FancySettlementModal from "@/components/FancySettlementModal";
import * as Dialog from "@radix-ui/react-dialog";

const LIMIT = 100;
const DEFAULT_OFFSET = 0;

const MARKETS_FOR_FANCY = [
  "1st Innings 6 Overs Line",
  "2nd Innings 6 Overs Line",
  "3rd Innings 6 Overs Line",
  "1st Innings 50 Overs Line",
  "2nd Innings 50 Overs Line",
  "3rd Innings 50 Overs Line",
  "1st Innings 40 Overs Line",
  "2nd Innings 40 Overs Line",
  "3rd Innings 40 Overs Line",
  "1st Innings 30 Overs Line",
  "2nd Innings 30 Overs Line",
  "3rd Innings 30 Overs Line",
  "1st Innings 20 Overs Line",
  "2nd Innings 20 Overs Line",
  "3rd Innings 20 Overs Line",
  "1st Innings 10 Overs Line",
  "2nd Innings 10 Overs Line",
  "3rd Innings 10 Overs Line",
  "Over By Over",
  "Ball By Ball",
  "Normal",
  "khado",
  "meter",
  "fancy1",
  "oddeven",
];

function getOrderedTabKeys(data) {
  if (!data || typeof data !== "object") return [];
  const keysWithData = Object.keys(data).filter(
    (k) => Array.isArray(data[k]) && data[k].length > 0
  );
  const ordered = [];
  for (const name of MARKETS_FOR_FANCY) {
    if (keysWithData.includes(name)) ordered.push(name);
  }
  if (keysWithData.includes("others")) ordered.push("others");
  for (const k of keysWithData) {
    if (!ordered.includes(k)) ordered.push(k);
  }
  return ordered;
}

export default function FancySettlement() {
  const [fancyData, setFancyData] = useState(null);
  const [tabKeys, setTabKeys] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalMode, setModalMode] = useState("declare");
  const [selectedRow, setSelectedRow] = useState(null);

  // Per-bet void state
  const [expandedRow, setExpandedRow] = useState(null);
  const [openBets, setOpenBets] = useState([]);
  const [betsLoading, setBetsLoading] = useState(false);
  const [voidConfirm, setVoidConfirm] = useState(null);

  const fetchFanmatches = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.FANMATCHES, {
        params: { limit: LIMIT, offset: DEFAULT_OFFSET },
      });
      if (response.data?.success && response.data.data && typeof response.data.data === "object") {
        const data = response.data.data;
        setFancyData(data);
        const ordered = getOrderedTabKeys(data);
        setTabKeys(ordered);
        setActiveTab((prev) => (ordered.length > 0 && !ordered.includes(prev) ? ordered[0] : prev));
      } else {
        setFancyData({});
        setTabKeys([]);
      }
    } catch (error) {
      if (!silent) console.error("Error fetching fancy matches:", error);
      setFancyData({});
      setTabKeys([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchFanmatches();
    const interval = setInterval(() => fetchFanmatches(true), 2000);
    return () => clearInterval(interval);
  }, []);

  const rows = (activeTab && fancyData && Array.isArray(fancyData[activeTab]))
    ? fancyData[activeTab]
    : [];

  const handleDeclare = (row) => {
    setSelectedRow({ ...row, marketType: row.marketType || activeTab });
    setModalMode("declare");
    setModalShow(true);
  };

  const handleVoid = (row) => {
    setSelectedRow({ ...row, marketType: row.marketType || activeTab });
    setModalMode("void");
    setModalShow(true);
  };

  const handleSubmit = async () => {
    setModalShow(false);
    setSelectedRow(null);
  };

  // ── Per-bet expand ──
  const getRowKey = (row) =>
    `${row.matchId}-${row.marketType || activeTab}-${row.gameType}-${row.selectionName || ""}`;

  const handleExpand = async (row) => {
    const key = getRowKey(row);
    if (expandedRow === key) {
      setExpandedRow(null);
      setOpenBets([]);
      return;
    }
    setExpandedRow(key);
    setBetsLoading(true);
    try {
      const resp = await getOpenBetsForMarket({
        match_id: row.matchId,
        market_type: row.marketType || activeTab,
        game_type: row.gameType || "FAN",
        selection_name: row.selectionName || undefined,
      });
      setOpenBets(resp?.success ? resp.data : []);
    } catch {
      setOpenBets([]);
    } finally {
      setBetsLoading(false);
    }
  };

  const handleVoidSingleBet = (bet) => {
    setVoidConfirm({ ...bet, loading: false });
  };

  const confirmVoidSingle = async () => {
    if (!voidConfirm) return;
    setVoidConfirm(prev => ({ ...prev, loading: true }));
    try {
      const resp = await voidSingleOpenBet({ bet_id: voidConfirm.id });
      if (resp?.success) {
        alert("Bet voided successfully for user: " + (voidConfirm.username || voidConfirm.userId));
      } else {
        alert("Failed: " + (resp?.error || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + (err?.response?.data?.error || err.message));
    } finally {
      setVoidConfirm(null);
      fetchFanmatches(true);
      setExpandedRow(null);
      setOpenBets([]);
    }
  };

  return (
    <Layout>
      <div className="p-4 w-full mx-auto bg-[#171b36] min-h-screen">
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)" }}
          className="shadow-sm px-4 pt-4"
        >
          <div className="flex justify-between items-center mb-1">
            <div style={{ color: "#cae0e8", fontSize: "24px" }} className="font-semibold">
              Fancy Settlement
            </div>
            <button
              type="button"
              onClick={() => fetchFanmatches()}
              disabled={loading}
              className="px-4 py-2 bg-[#11374a] text-white text-[14px] font-bold hover:bg-[#1a4f66] transition-colors disabled:opacity-50"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Tabs */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px" }}
          className="shadow-sm px-4 pt-4 pb-2"
        >
          <div className="flex flex-wrap gap-2 mb-4">
            {tabKeys.map((key) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); setExpandedRow(null); }}
                className={`px-4 py-2 text-[12px] font-bold transition-colors ${
                  activeTab === key ? "bg-[#333d79ff] text-white" : "bg-gray-600 text-white"
                }`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div
          style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", marginTop: "-8px", minHeight: "500px" }}
          className="shadow-sm p-4"
        >
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden">
              <table className="w-full text-sm table-fixed">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[25%]">Match Title</th>
                    <th className="text-left px-4 py-2 text-white font-bold text-[15px] w-[25%]">
                      {MARKETS_FOR_FANCY.includes(activeTab) ? "Market Name" : "Market Type"}
                    </th>
                    <th className="text-right px-4 py-2 text-white font-semibold w-[12%]">Total Bets</th>
                    <th className="text-center px-4 py-2 text-white font-bold text-[15px] w-[25%]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={4} className="text-center text-white py-4">Loading...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={4} className="text-center text-white py-4">No matches found</td></tr>
                  ) : (
                    rows.map((row, index) => (
                      <React.Fragment key={`${row.matchId}-${row.eventId}-${index}`}>
                        <tr className={`transition-colors ${index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""}`}>
                          <td className="py-2 px-4 text-[#2ce5dd] font-medium truncate">
                            {row.eventName || row.matchTitle || "-"}
                          </td>
                          <td className="py-2 px-4 text-white font-bold truncate">
                            {MARKETS_FOR_FANCY.includes(activeTab) ? (row.selectionName || "-") : (row.marketType ?? activeTab ?? "-")}
                          </td>
                          <td className="py-2 px-4 text-right text-white font-bold">{row.totalBets ?? 0}</td>
                          <td className="py-2 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleDeclare(row)}
                                className="px-3 py-1 bg-[#11374a] text-white text-[12px] font-bold hover:bg-[#1a4f66] transition-colors"
                              >
                                Declare
                              </button>
                              <button
                                onClick={() => handleVoid(row)}
                                className="px-3 py-1 bg-gray-600 text-white text-[12px] font-bold hover:bg-gray-500 transition-colors"
                              >
                                Void
                              </button>
                              <button
                                onClick={() => handleExpand(row)}
                                className="px-3 py-1 bg-[#c2410c] text-white text-[12px] font-bold hover:bg-[#ea580c] transition-colors"
                              >
                                {expandedRow === getRowKey(row) ? "Hide" : "Per Bet"}
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded per-bet view */}
                        {expandedRow === getRowKey(row) && (
                          <tr>
                            <td colSpan={4} className="px-4 pb-3 pt-0">
                              <div className="bg-[#0d1025] border border-[#252d5c] p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[#cae0e8] text-[14px] font-bold">
                                    Individual Bets &mdash; {row.selectionName || row.marketType || activeTab}
                                  </span>
                                  <span className="text-gray-500 text-[12px]">
                                    {openBets.length} bet{openBets.length !== 1 ? "s" : ""}
                                  </span>
                                </div>
                                {betsLoading ? (
                                  <div className="text-gray-400 text-center py-6 text-sm">Loading bets...</div>
                                ) : openBets.length === 0 ? (
                                  <div className="text-gray-400 text-center py-6 text-sm">No open bets found</div>
                                ) : (
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-[#252d5c]">
                                        <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">Bet ID</th>
                                        <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">User</th>
                                        <th className="text-left px-3 py-2 text-gray-400 font-semibold text-[12px]">Selection</th>
                                        <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Bet Type</th>
                                        <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Odds</th>
                                        <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Stake</th>
                                        <th className="text-right px-3 py-2 text-gray-400 font-semibold text-[12px]">Liability</th>
                                        <th className="text-center px-3 py-2 text-gray-400 font-semibold text-[12px]">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {openBets.map((bet, bIdx) => (
                                        <tr key={bet.id} className={`border-b border-[#1a1f3d] ${bIdx % 2 === 0 ? "bg-[rgba(255,255,255,0.02)]" : ""}`}>
                                          <td className="px-3 py-2.5 text-[#2ce5dd] font-medium">#{bet.id}</td>
                                          <td className="px-3 py-2.5 text-white font-medium">{bet.username}</td>
                                          <td className="px-3 py-2.5 text-white">{bet.selectionName || "-"}</td>
                                          <td className="px-3 py-2.5 text-center">
                                            <span className={`inline-block min-w-[40px] px-2 py-0.5 text-[11px] font-bold text-center text-white ${
                                              bet.betType === "back" || bet.betType === "yes" || bet.betType === "Yes"
                                                ? "bg-[#3b82f6]" : "bg-[#ec4899]"
                                            }`}>
                                              {(bet.betType || "-").toUpperCase()}
                                            </span>
                                          </td>
                                          <td className="px-3 py-2.5 text-right text-white">{bet.odds ?? "-"}</td>
                                          <td className="px-3 py-2.5 text-right text-white">{bet.stakeAmount != null ? bet.stakeAmount.toFixed(2) : "-"}</td>
                                          <td className="px-3 py-2.5 text-right text-white">{bet.liability != null ? bet.liability.toFixed(2) : "-"}</td>
                                          <td className="px-3 py-2.5 text-center">
                                            <button
                                              onClick={() => handleVoidSingleBet(bet)}
                                              className="px-3 py-1.5 bg-[#c2410c] text-white text-[12px] font-bold hover:bg-[#ea580c] transition-colors"
                                            >
                                              Void Bet
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <FancySettlementModal
        show={modalShow}
        onHide={() => { setModalShow(false); setSelectedRow(null); }}
        mode={modalMode}
        row={selectedRow}
        onSubmit={handleSubmit}
      />

      {/* Void single bet confirmation */}
      <Dialog.Root open={!!voidConfirm} onOpenChange={(open) => { if (!open) setVoidConfirm(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/60" style={{ zIndex: 9999 }} />
          <Dialog.Content
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#222951] border border-[#333d79] p-6 shadow-xl w-[calc(100vw-2rem)] max-w-[480px]"
            style={{ zIndex: 10000 }}
          >
            <Dialog.Title className="text-white text-lg font-bold mb-4">Void Single Bet</Dialog.Title>
            <div className="border-l-4 border-[#f59e0b] bg-[rgba(245,158,11,0.08)] p-4 mb-5">
              <div className="text-white text-sm leading-relaxed">
                <p>
                  Void bet <strong>#{voidConfirm?.id}</strong> for user{" "}
                  <span className="text-[#2ce5dd]">{voidConfirm?.username}</span>?
                </p>
                <p className="mt-2 text-gray-300">
                  Selection: <strong className="text-white">{voidConfirm?.selectionName}</strong> |
                  Stake: <strong className="text-white">{voidConfirm?.stakeAmount?.toFixed(2)}</strong> |
                  Odds: <strong className="text-white">{voidConfirm?.odds}</strong>
                </p>
                <p className="mt-2 text-gray-300">The stake will be refunded. This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Dialog.Close asChild>
                <button className="px-4 py-2 bg-gray-600 text-white text-sm font-bold hover:bg-gray-500 transition-colors">Cancel</button>
              </Dialog.Close>
              <button
                onClick={confirmVoidSingle}
                disabled={voidConfirm?.loading}
                className="px-4 py-2 bg-[#b91c1c] text-white text-sm font-bold hover:bg-[#dc2626] transition-colors disabled:opacity-50"
              >
                {voidConfirm?.loading ? "Processing..." : "Confirm Void"}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Layout>
  );
}
